<script setup lang="ts">
import { onMounted, onUnmounted, ref, createApp } from 'vue'
import { onLaunch, onShow, onHide } from '@dcloudio/uni-app'
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only'
import store, { useGameStore, useUserStore, useThemeStore } from '@/store'
import { useLoginStore } from '@/store/modules/login'
import { getCheckinConfig, getPlayerCheckinInfo } from '@/service'
import i18n from '@/locale/index'
import dayjs from 'dayjs'
// @ts-expect-error Vue 组件没有类型定义
import CustomLoading from '@/components/CustomLoading.vue'
// @ts-expect-error Vue 组件没有类型定义
import LoginPopup from '@/components/LoginPopup.vue'

const userStore = useUserStore()
const gameStore = useGameStore()
const loginStore = useLoginStore()
const themeStore = useThemeStore()

// 签到相关数据
const checkinConfigData = ref<any>(null)

// 签到弹窗刷新逻辑
const refreshCheckinPopup = async () => {
  await getPlayerCheckinInfoData()
  await getCheckinConfigData()
  if (!checkinConfigData.value?.ID) {
    userStore.checkinPopup = false
    return
  }
  if (!userStore.firstCheckin && checkinConfigData.value?.ID) {
    userStore.firstCheckin = true
    userStore.checkinPopup = true
  }
}

// 获取签到配置数据
const getCheckinConfigData = async () => {
  const res = await getCheckinConfig()
  if (res.code === 0) {
    checkinConfigData.value = res.data.checkin
  }
}

// 获取玩家签到信息
const getPlayerCheckinInfoData = async () => {
  const res = await getPlayerCheckinInfo()
  if (res.code === 0) {
    // 这里可以处理玩家签到信息，如果需要的话
  }
}

// 全局 loading 实例
let loadingApp = null
let loadingInstance = null

// 全局 login popup 实例
let loginApp = null
let loginInstance = null

onMounted(() => {
  // 创建 CustomLoading 应用实例
  const appElement = document.getElementById('app')
  if (appElement) {
    // 创建 loading 容器
    const loadingContainer = document.createElement('div')
    loadingContainer.id = 'custom-loading-container'
    appElement.appendChild(loadingContainer)

    // 创建 Vue 应用实例
    loadingApp = createApp(CustomLoading)
    loadingInstance = loadingApp.mount(loadingContainer)

    // 将 loading 方法挂载到 uni 对象上
    ;(uni as any).$loading = {
      show: (options) => {
        console.log('uni.$loading.show called', options)
        loadingInstance?.show(options)
      },
      hide: () => {
        console.log('uni.$loading.hide called')
        loadingInstance?.hide()
      },
    }

    console.log('CustomLoading: 已通过 JS 挂载到 #app 下')
  } else {
    console.error('CustomLoading: 未找到 #app 元素')
  }

  // 创建 LoginPopup 应用实例
  if (appElement) {
    // 创建 login popup 容器
    const loginContainer = document.createElement('div')
    loginContainer.id = 'custom-login-container'
    appElement.appendChild(loginContainer)

    // 创建 Vue 应用实例
    loginApp = createApp(LoginPopup, {
      onClose: () => {
        console.log('LoginPopup: 收到关闭事件')
        loginStore.hideLogin()
      },
      onLoginSuccess: async () => {
        console.log('LoginPopup: 收到登录成功事件')
        loginStore.handleLoginSuccess()
        await refreshCheckinPopup()
        gameStore.loadGameCategoryList()
        gameStore.loadCurrencyList()
      },
    })

    // 安装必要的插件
    loginApp.use(store)
    loginApp.use(i18n)

    loginInstance = loginApp.mount(loginContainer)

    console.log('LoginPopup: 已通过 JS 挂载到 #app 下')
  } else {
    console.error('LoginPopup: 未找到 #app 元素')
  }
})

onLaunch(async () => {
  console.log('App Launch')
  await userStore.getUserInfoData()
  // 每天凌晨0点设置为 false
  const now = new Date()
  const userLoginTime = userStore.userLoginTime
  console.log(userLoginTime, dayjs(userLoginTime).isBefore(dayjs().subtract(1, 'day'), 'day'))
  // 如果用户没有登录时间，或者用户登录时间是当前时间前一天，则设置为 false
  if (!userLoginTime || dayjs(userLoginTime).isBefore(dayjs().subtract(1, 'day'), 'day')) {
    userStore.firstCheckin = false
  }
  userStore.setUserLoginTime()
  userStore.checkinPopup = false
  gameStore.loadCurrencyList()

  // 预加载
  const tabbarPages = [
    '/',
    '/pages/activity/index',
    '/pages/recommend/index',
    '/pages/mine/mine',
    '/pages/game/game',
  ]
  tabbarPages.forEach((page) => {
    uni.preloadPage({
      url: page,
    })
  })
})
onLoad((query) => {})
onShow(() => {
  userStore.updateUserInfo()
  console.log('App Show')
})
onHide(() => {
  console.log('App Hide')
})

// 组件卸载时清理资源
onUnmounted(() => {
  console.log('App Unmounted: 清理 CustomLoading 资源')

  // 清理全局方法
  if ((uni as any).$loading) {
    delete (uni as any).$loading
  }

  // 卸载 Vue 应用实例
  if (loadingApp) {
    loadingApp.unmount()
    loadingApp = null
  }

  // 移除 DOM 容器
  const loadingContainer = document.getElementById('custom-loading-container')
  if (loadingContainer) {
    loadingContainer.remove()
  }

  loadingInstance = null
  console.log('CustomLoading: 资源清理完成')

  // 清理 LoginPopup 资源
  console.log('App Unmounted: 清理 LoginPopup 资源')

  // 卸载 Vue 应用实例
  if (loginApp) {
    loginApp.unmount()
    loginApp = null
  }

  // 移除 DOM 容器
  const loginContainer = document.getElementById('custom-login-container')
  if (loginContainer) {
    loginContainer.remove()
  }

  loginInstance = null
  console.log('LoginPopup: 资源清理完成')
})
</script>

<style lang="scss">
/* stylelint-disable selector-type-no-unknown */
:root {
  box-sizing: border-box;
}
@font-face {
  font-family: 'Montserrat-Regular';
  src: url('@/static/fonts/Montserrat-Regular-8.otf');
}
body {
  font-family: 'Montserrat-Regular';
}
*,
::before,
::after {
  box-sizing: inherit;
}

button::after {
  border: none;
}

swiper,
scroll-view {
  flex: 1;
  height: 100%;
  overflow: hidden;
}

image {
  display: block;
  width: 100%;
  height: 100%;
}

// 单行省略，优先使用 unocss: text-ellipsis
.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// 两行省略
.ellipsis-2 {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

// 三行省略
.ellipsis-3 {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

// 隐藏滚动条
::-webkit-scrollbar {
  display: none;
}
.primary-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 12px;
  font-weight: 700;
  color: #fff !important;
  text-align: center;
  background: linear-gradient(1turn, #0c5eff 0.8%, #1398f9) !important;
  border-radius: 10px;
  box-shadow:
    0 3px 28px rgba(34, 131, 246, 0.4),
    inset 0 3px 3px hsla(0, 0%, 100%, 0.30196078431372547);
}
.warning-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 12px;
  font-weight: 700;
  color: #fff !important;
  text-align: center;
  background: linear-gradient(3360deg, #db0a49 0.8%, #fb2b57) !important;
  border-radius: 10px;
  box-shadow:
    0 3px 16px rgba(255, 35, 65, 0.5019607843137255),
    inset 0 4px 3px hsla(0, 0%, 100%, 0.30196078431372547);
}

:deep(.dark) {
  .line-gradient {
    background: linear-gradient(90deg, #43536d00 5%, #43536d 50%, #43536d00);
  }
  .checkin-item-status {
    background: rgba(255, 255, 255, 0.1);
  }
  .swiper-bg {
    color: #2283f6;
    background: #fff;
  }
  .selectField-item {
    background: #2a3546;
  }
  .login-popup {
    background: #131922;
    .switch-bar {
      background: linear-gradient(to right, #37465c4d, #37465c4d);
      .active {
        color: #fff;
        background: linear-gradient(to right, #548dff, #007aff);
      }
    }
    .login-form {
      .login-input {
        background: #181f2b;
        border: 1px solid #2a3546;
      }
    }
  }
  .table-striped {
    tbody tr:nth-child(odd) {
      background-color: rgba(24, 149, 240, 0.2);
    }

    tbody tr:nth-child(even) {
      background-color: rgba(255, 255, 255, 0.02);
    }

    tbody tr:hover {
      background-color: rgba(24, 149, 240, 0.2);
    }
  }

  td,
  th {
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }
}

:deep(.light) {
  .table-striped {
    tbody tr:nth-child(odd) {
      background-color: rgba(0, 0, 240, 0.05);
    }

    tbody tr:nth-child(even) {
      background-color: transparent;
    }

    tbody tr:hover {
      background-color: rgba(24, 149, 240, 0.05);
    }
  }

  td,
  th {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
  .line-gradient {
    background: linear-gradient(90deg, #43536d00 5%, #c9e2ff 50%, #43536d00);
  }
  .checkin-item-status {
    background: rgba(0, 0, 0, 0.1);
  }
  .selectField-item {
    color: #fff;
    background: #007aff;
  }
  .swiper-bg {
    color: #fff;
    background: #2283f6;
  }
  .login-popup {
    background: #f3f6fa;
    .switch-bar {
      background: linear-gradient(to right, #548dff, #007aff);
      .active {
        color: #007aff;
        background: #fff;
      }
    }
    .login-form {
      .login-input {
        background: #fff;
        border: 1px solid #e5e5e5;
      }
    }
  }
}
div,
span {
  word-break: break-word !important;
}
</style>
